import { useEffect, useState, useMemo } from "react";
import { usePathname } from "../../lib/router";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Calendar } from "../../components/ui/calendar";
import { Loader2, CheckCircle, AlertCircle, Clock, Calendar as CalendarIcon, User, MapPin } from "lucide-react";
import { formatTime, formatDate } from "../utils/date";
import { useLanguage } from "../../contexts/LanguageContext";
import { apptLabels } from "./labels";
import { ptBR, enUS } from "date-fns/locale";

export default function PublicReschedulePage() {
  const { lang } = useLanguage();
  const t = apptLabels[lang].publicReschedule;
  const calendarLocale = lang === "pt" ? ptBR : enUS;

  const path = usePathname();
  // Expecting URL: /remarcar/:token
  const token = path.split("/")[2];
  
  const [appt, setAppt] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [date, setDate] = useState<Date>(() => new Date());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [slots, setSlots] = useState<any[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  useEffect(() => {
    if (token) fetchAppt();
  }, [token]);

  const fetchAppt = async () => {
    try {
      const res = await fetch(`/api/public/appointments/${token}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao carregar");
      setAppt(data);
      setSelectedEmployeeId(data.employee_id);
      // Initialize calendar date to appt date or today
      const apptDate = new Date(data.start_datetime.replace(" ", "T"));
      if (apptDate > new Date()) {
        setDate(apptDate);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appt && date && selectedEmployeeId) {
      fetchSlots();
    }
  }, [appt, date, selectedEmployeeId]);

  const fetchSlots = async () => {
    setLoadingSlots(true);
    setSelectedSlot(null);
    try {
      const pad2 = (n: number) => String(n).padStart(2, "0");
      const dateStr = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
      const res = await fetch(`/api/available-slots?employee_id=${selectedEmployeeId}&specialty_id=${appt.specialty_id}&start=${dateStr}&token=${token}`);
      const data = await res.json();
      if (res.ok) {
        setSlots(data.filter((s: any) => s.available));
      }
    } catch (e) {
      console.error("Error fetching slots", e);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/public/appointments/${token}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          start_datetime: selectedSlot,
          employee_id: selectedEmployeeId
        })
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json();
        alert(data.error || "Erro ao remarcar");
      }
    } catch (e) {
      alert("Erro de conexão");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-50"><Loader2 className="animate-spin h-8 w-8 text-[#71B27B]" /></div>;
  
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-neutral-50 font-sans">
      <Card className="max-w-md w-full border-0 shadow-2xl">
        <CardContent className="pt-8 text-center">
          <div className="bg-red-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-neutral-900">{t.invalidLink}</h2>
          <p className="text-neutral-600 mb-6">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()} className="w-full h-11">{t.tryAgain}</Button>
        </CardContent>
      </Card>
    </div>
  );

  if (success) return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-neutral-50 font-sans">
      <Card className="max-w-md w-full border-0 shadow-2xl overflow-hidden">
        <div className="h-2 bg-emerald-500 w-full" />
        <CardContent className="pt-8 text-center p-8">
          <div className="bg-emerald-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-neutral-900">{t.rescheduled}</h2>
          <p className="text-neutral-600 mb-8">{t.rescheduledSubtitle}</p>
          
          <div className="bg-neutral-50 rounded-2xl p-6 text-left border mb-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-white p-2 rounded-lg border shadow-sm"><CalendarIcon className="h-5 w-5 text-[#71B27B]" /></div>
              <div>
                <p className="text-xs font-bold text-neutral-400 uppercase tracking-widest mb-1">{t.newDateLabel}</p>
                <p className="text-lg font-bold text-neutral-800">{formatDate(selectedSlot!)}</p>
                <p className="text-sm text-neutral-500">{formatTime(selectedSlot!)}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="bg-white p-2 rounded-lg border shadow-sm"><User className="h-5 w-5 text-[#71B27B]" /></div>
              <div>
                <p className="text-xs font-bold text-neutral-400 uppercase tracking-widest mb-1">{t.professionalLabel}</p>
                <p className="text-lg font-bold text-neutral-800">{appt.professionals?.find((p: any) => p.id === selectedEmployeeId)?.name || appt.employee_name}</p>
              </div>
            </div>
          </div>

          <p className="text-xs text-neutral-400 italic">{t.closedPage}</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50 font-sans">
      {/* Hero Header */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 bg-[#71B27B] rounded-xl flex items-center justify-center text-white font-semibold text-xl shadow-lg shadow-emerald-200">
               {appt.company_name?.[0]}
             </div>
             <div>
               <h1 className="font-semibold text-neutral-900 leading-none">{appt.company_name}</h1>
               <p className="text-[10px] text-neutral-400 uppercase font-semibold tracking-widest mt-1">Plataforma de Saúde</p>
             </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 py-8 md:p-8 space-y-8 pb-32">
        {/* Welcome Section */}
        <div className="space-y-2">
          <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 leading-tight">
            {t.welcome(appt.client_name?.split(" ")[0])}
          </h2>
          <p className="text-neutral-500 text-lg">{t.subtitle}</p>
        </div>

        {/* Current Appointment Card */}
        <Card className="border-0 shadow-xl shadow-emerald-900/5 bg-white overflow-hidden rounded-3xl">
          <CardHeader className="bg-neutral-900 text-white p-6 md:px-8">
            <CardTitle className="text-sm font-semibold uppercase tracking-widest opacity-60">{t.currentAppt}</CardTitle>
          </CardHeader>
          <CardContent className="p-8 pt-8 md:p-12 md:pt-10 grid md:grid-cols-2 gap-10">
            <div className="flex items-center gap-5">
              <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100">
                <CalendarIcon className="h-8 w-8 text-[#71B27B]" />
              </div>
              <div>
                <p className="text-xl font-semibold text-neutral-900 leading-none mb-2">{formatDate(appt.start_datetime)}</p>
                <p className="text-neutral-500 font-bold flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> {formatTime(appt.start_datetime)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100">
                <User className="h-8 w-8 text-amber-600" />
              </div>
              <div>
                <p className="text-xl font-semibold text-neutral-900 leading-none mb-2">{appt.employee_name}</p>
                <p className="text-neutral-500 font-bold uppercase text-[10px] tracking-widest">{appt.specialty_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selection Grid */}
        <div className="space-y-12">
          {/* Step 1: Professional */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-neutral-900 rounded-lg flex items-center justify-center text-white font-bold text-sm">1</div>
              <h3 className="text-xl font-semibold text-neutral-900 tracking-tight">{t.step1}</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {appt.professionals?.map((p: any) => {
                const isSelected = selectedEmployeeId === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedEmployeeId(p.id)}
                    className={`
                      px-4 py-2 rounded-xl border text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2
                      ${isSelected 
                        ? "border-neutral-900 bg-white ring-1 ring-neutral-900 text-neutral-900 shadow-md" 
                        : "bg-white border-neutral-200 text-neutral-400 hover:border-neutral-300 hover:bg-neutral-50/50"}
                    `}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color || '#888888' }} />
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_auto] gap-12 pt-4 border-t border-neutral-100">
            <div className="space-y-6">
               <div className="flex items-center gap-3 mb-4">
                 <div className="w-8 h-8 bg-neutral-900 rounded-lg flex items-center justify-center text-white font-bold text-sm">2</div>
                 <h3 className="text-xl font-semibold text-neutral-900 tracking-tight">{t.step2}</h3>
               </div>
               <div className="bg-white p-6 rounded-[32px] shadow-xl shadow-neutral-200/50 border border-neutral-100 overflow-hidden">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  className="w-full"
                  disabled={{ before: new Date() }}
                  locale={calendarLocale}
                />
             </div>
          </div>

            <div className="space-y-6 lg:min-w-[340px]">
               <div className="flex items-center gap-3 mb-4">
                 <div className="w-8 h-8 bg-neutral-900 rounded-lg flex items-center justify-center text-white font-bold text-sm">3</div>
                 <h3 className="text-xl font-semibold text-neutral-900 tracking-tight">{t.step3}</h3>
               </div>
               
               <div className="bg-white p-6 rounded-[32px] shadow-xl shadow-neutral-200/50 border border-neutral-100 min-h-[360px] flex flex-col">
                 {loadingSlots ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-4 py-20">
                       <div className="relative">
                         <Loader2 className="animate-spin h-12 w-12 text-[#71B27B]" />
                         <div className="absolute inset-0 flex items-center justify-center">
                           <div className="w-6 h-6 bg-emerald-50 rounded-full" />
                         </div>
                       </div>
                       <p className="text-neutral-400 font-bold animate-pulse text-xs uppercase tracking-widest">{t.searching}</p>
                    </div>
                  ) : slots.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center gap-6 py-20">
                       <div className="bg-neutral-50 p-6 rounded-full"><AlertCircle className="h-10 w-10 text-neutral-300" /></div>
                       <p className="text-neutral-400 font-bold px-6 leading-relaxed">{t.noSlots}</p>
                       <p className="text-[10px] text-neutral-300 uppercase font-semibold tracking-widest">{t.tip}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {slots.map((slot) => {
                        const isSelected = selectedSlot === slot.start;
                        return (
                          <button
                            key={slot.start}
                            onClick={() => setSelectedSlot(slot.start)}
                            className={`
                              h-16 rounded-2xl font-semibold text-base transition-all border-2
                              ${isSelected 
                                ? "bg-[#71B27B] border-[#71B27B] text-white shadow-xl shadow-emerald-200 scale-105 z-10" 
                                : "bg-white border-neutral-50 text-neutral-700 hover:border-emerald-200 hover:bg-emerald-50/30"}
                            `}
                          >
                            {formatTime(slot.start)}
                          </button>
                        );
                      })}
                    </div>
                  )}
             </div>
          </div>
        </div>
        </div>
      </div>

      {/* Floating Action Bar */}
      <div className={`fixed bottom-0 left-0 right-0 p-6 bg-white/80 backdrop-blur-xl border-t z-40  shadow-[0_-20px_40px_rgba(0,0,0,0.05)] ${selectedSlot ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-6">
          <div className="hidden md:block">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-1">{t.selected}</p>
            <p className="text-lg font-semibold text-neutral-900">{selectedSlot ? `${formatDate(selectedSlot)} ${t.at} ${formatTime(selectedSlot)}` : "-"}</p>
          </div>
          <Button 
            onClick={handleConfirm}
            disabled={saving || !selectedSlot}
            className="flex-1 md:flex-none md:min-w-[240px] h-16 bg-[#71B27B] hover:bg-[#5E9A68] text-white font-semibold text-lg rounded-2xl shadow-xl shadow-emerald-200 transition-all active:scale-95"
          >
            {saving ? <Loader2 className="animate-spin mr-3 h-6 w-6" /> : null}
            {saving ? t.processing : t.confirm}
          </Button>
        </div>
      </div>
    </div>
  );
}
